import { API_BASE_URL, fetchApi } from '@/services/api-client';

export type ImportType = 'students' | 'staff';

export type AnalyzedRowDisposition = 'add' | 'skip' | 'error';

export type AnalyzedRow = {
  rowNumber: number;
  disposition: AnalyzedRowDisposition;
  values: Record<string, string>;
  skipReason?: string;
  errors?: string[];
  notes?: string[];
};

export type ImportSummary = {
  add: number;
  skip: number;
  error: number;
};

export type ImportPreview = {
  summary: ImportSummary;
  rows: AnalyzedRow[];
};

export type ImportResultError = {
  rowNumber: number;
  message: string;
};

export type ImportResult = {
  created: number;
  skipped: number;
  errors: ImportResultError[];
  invited: number;
};

const IMPORT_PATH = '/admin/import';

function buildFormData(file: File): FormData {
  const fd = new FormData();
  fd.append('file', file);
  return fd;
}

export const importApi = {
  preview: (type: ImportType, file: File): Promise<ImportPreview> =>
    fetchApi<ImportPreview>(`${IMPORT_PATH}/${type}/preview`, {
      method: 'POST',
      body: buildFormData(file),
      // Parsing a whole spreadsheet runs well past the default request timeout.
      timeoutMs: 180_000,
    }),

  commit: (type: ImportType, file: File, invite?: boolean): Promise<ImportResult> =>
    fetchApi<ImportResult>(`${IMPORT_PATH}/${type}/commit?invite=${invite ?? true}`, {
      method: 'POST',
      body: buildFormData(file),
      // Committing an import writes many rows (and may send invitations) — same reason.
      timeoutMs: 180_000,
    }),

  downloadTemplate: async (type: ImportType): Promise<void> => {
    const url = `${API_BASE_URL}${IMPORT_PATH}/${type}/template`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to download template');
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `import-template-${type}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  },
};
